import { DetailCard, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import {
  PROCEDURE_INFO_TITLE,
  VALUE_CHANGED_BG,
  VALUE_CHANGED_LABEL,
  VALUE_CHANGED_TEXT,
  type ApprovalStatus,
  type ProcedureFieldState,
} from "../utils/constants";
import ProcedureAccordion from "./ProcedureAccordion";

export type AgentProcedureDiffMap = Record<
  string,
  Set<keyof ProcedureFieldState>
>;

export type { ProcedureFieldState } from "../utils/constants";

interface ProcedureInformationProps {
  jCodes: JCodeDetails[];
  procedureStatuses: Record<string, ApprovalStatus>;
  onProcedureStatusChange: (id: string, status: ApprovalStatus) => void;
  onProcedureFieldStateUpdate: (
    id: string,
    state: ProcedureFieldState | null,
  ) => void;
  onHasAnyChangeUpdate?: (changesMap: Record<string, boolean>) => void;
  agentProcedureDiffByJCode?: AgentProcedureDiffMap;
  resetTrigger?: number;
  /** Per–J-code id: reject loaded from persisted `human_validated` (suppress feedback mode). */
  procedureServerRejectHydrated?: Record<string, boolean>;
  /** Per–J-code id: required fields currently holding invalid values (row-header asterisk). */
  procedureRequiredInvalidKeys?: Record<string, Set<keyof ProcedureFieldState>>;
}

const ProcedureInformation = ({
  jCodes,
  procedureStatuses,
  onProcedureStatusChange,
  onProcedureFieldStateUpdate,
  onHasAnyChangeUpdate,
  agentProcedureDiffByJCode,
  resetTrigger = 0,
  procedureServerRejectHydrated = {},
  procedureRequiredInvalidKeys,
}: ProcedureInformationProps) => {
  const [expandedId, setExpandedId] = useState<string | undefined>(undefined);
  const [procedureIdsWithChanges, setProcedureIdsWithChanges] = useState<
    Record<string, boolean>
  >({});

  const handleToggle = useCallback((id: string | undefined) => {
    setExpandedId((prev) => (prev === id ? undefined : id));
  }, []);

  const handleProcedureChange = useCallback(
    (id: string, hasChange: boolean) => {
      setProcedureIdsWithChanges((prev) =>
        prev[id] === hasChange ? prev : { ...prev, [id]: hasChange },
      );
    },
    [],
  );

  const hasAnyProcedureChange = Object.values(procedureIdsWithChanges).some(
    Boolean,
  );
  const hasAnyAgentProcedureDiff = Object.values(
    agentProcedureDiffByJCode ?? {},
  ).some((s) => s.size > 0);

  useEffect(() => {
    onHasAnyChangeUpdate?.(procedureIdsWithChanges);
  }, [procedureIdsWithChanges, onHasAnyChangeUpdate]);

  if (!jCodes.length) return null;

  return (
    <DetailCard
      title={PROCEDURE_INFO_TITLE}
      badge={
        hasAnyProcedureChange || hasAnyAgentProcedureDiff ? (
          <Tags
            label={VALUE_CHANGED_LABEL}
            backgroundColor={VALUE_CHANGED_BG}
            textColor={VALUE_CHANGED_TEXT}
          />
        ) : undefined
      }
      headerClassName="font-semibold leading-[1.375rem] text-[0.875rem] text-primaryGray-1 [font-family:Lato]"
      bodyClassName="p-0 m-0"
    >
      <div className="flex flex-col">
        {jCodes.map((jCode) => {
          const id = jCode.id ?? jCode.j_code ?? "";
          const jCodeStr = jCode.j_code?.trim().toUpperCase() ?? "";
          const agentKeys =
            jCodeStr && agentProcedureDiffByJCode
              ? agentProcedureDiffByJCode[jCodeStr]
              : undefined;
          return (
            <ProcedureAccordion
              key={id}
              jCode={jCode}
              isExpanded={expandedId === id}
              onToggle={() => handleToggle(id)}
              approvalStatus={procedureStatuses[id] ?? null}
              onApprovalStatusChange={(status) =>
                onProcedureStatusChange(id, status)
              }
              onProcedureChange={handleProcedureChange}
              onFieldStateUpdate={onProcedureFieldStateUpdate}
              agentDiffChangedKeys={agentKeys}
              resetTrigger={resetTrigger}
              serverRejectHydrated={procedureServerRejectHydrated[id] === true}
              requiredInvalidFieldKeys={procedureRequiredInvalidKeys?.[id]}
            />
          );
        })}
      </div>
    </DetailCard>
  );
};

export default ProcedureInformation;
